import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "control", "controlId", "description", "disabled", "error", "label", "optional", "readOnly", "required"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext, useRef } from 'react';
import { identifier } from '../../util/identifier';
import { FormFieldContext } from './FormField.context';
import { StyledFormField } from './FormField.styles';

/**
 * @related [`FormHelp`, `Label`]
 * @since 4.0.0
 */
export var FormField = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    control = _ref.control,
    controlId = _ref.controlId,
    description = _ref.description,
    disabled = _ref.disabled,
    error = _ref.error,
    label = _ref.label,
    optional = _ref.optional,
    readOnly = _ref.readOnly,
    required = _ref.required,
    rest = _objectWithoutProperties(_ref, _excluded);
  // parentContext comes from components that extend this, e.g. FieldSet
  var parentContext = useContext(FormFieldContext);
  // defaultId is used as the id for the control and the htmlFor for the label
  var defaultId = useRef(identifier()).current;
  // descriptionId is used for a field description, e.g. FormHelp
  var descriptionId = useRef(identifier()).current;
  // labelId is only necessary when there are multiple labels, e.g. AdornedInput
  var labelId = useRef(identifier()).current;
  var context = {
    controlId: typeof controlId !== 'undefined' ? controlId : defaultId,
    labelId: labelId,
    required: required
  };
  if (description) {
    // Use descriptionId from the parent context if it exists
    context.descriptionId = parentContext.descriptionId || descriptionId;
  }
  if (typeof optional !== 'undefined') {
    context.optional = optional;
  }
  if (typeof disabled !== 'undefined') {
    context.disabled = disabled;
  }
  if (typeof error !== 'undefined') {
    context.error = error;
  }
  if (typeof readOnly !== 'undefined') {
    context.readOnly = readOnly;
  }
  return /*#__PURE__*/React.createElement(StyledFormField, _extends({
    ref: forwardedRef
  }, rest), /*#__PURE__*/React.createElement(FormFieldContext.Provider, {
    value: context
  }, label, control, children, description));
});
FormField.displayName = 'FormField';
FormField.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * In general you will want to use the `control`, `description`, and `label` props instead
   * of `children`, but you can use `children` if you need a specialized layout. When using
   * `children`, however, not all ARIA properties can automatically be associated, so you must
   * verify that your specialized layout follow accessibility best practices.
   */
  children: PropTypes.node,
  /**
   * A single control node, e.g. Input, Select, Textarea.
   * When composing several different controls in one field, use a FieldSet.
   */
  control: PropTypes.node,
  /**
   * Identifier used for form controls and labels. This will be used as the `id` by
   * form controls and the `htmlFor` by Label components.
   *
   * If no `controlId` is specified, one will automatically be generated.
   */
  controlId: PropTypes.string,
  /**
   * A node that describes the form field, typically a FormHelp.
   * This will automatically associate the FormHelp with the field control using
   * [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute).
   */
  description: PropTypes.node,
  /**
   * A node that labels the form field, typically a Label component.
   */
  label: PropTypes.node,
  /**
   * Indicates the form field is optional in the label.
   */
  optional: PropTypes.bool,
  /**
   * Indicates the form field is required. Inherited by the nested form control and label.
   */
  required: PropTypes.bool,
  /**
   * Displays the form field in a disabled state. Inherited by the nested form control
   * and label.
   *
   * @since 8.9.0
   */
  disabled: PropTypes.bool,
  /**
   * Displays the form field in an error state. Inherited by the nested form control.
   *
   * @since 8.9.0
   */
  error: PropTypes.bool,
  /**
   * Displays the form field in a read-only state. Inherited by the nested form control.
   *
   * @since 8.27.0
   */
  readOnly: PropTypes.bool
} : {};